import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Users, Activity, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import api from '../../services/api';

/**
 * Format seconds to human-readable hours and minutes
 */
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0h 0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

/**
 * Activity Tracking Panel for Admin
 * Shows user activity reports with active/idle/offline time
 */
const ActivityTrackingPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(7);
  const [selectedUser, setSelectedUser] = useState(null);
  const [includeDaily, setIncludeDaily] = useState(false);

  // Load activity report
  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('days', days);
      if (selectedUser) params.append('user_id', selectedUser);
      if (includeDaily) params.append('include_daily', 'true');

      const response = await api.get(`/presence/activity/report?${params.toString()}`);
      setReport(response.data);
    } catch (err) {
      console.error('Error loading activity report:', err);
      setError(err.response?.data?.detail || 'Failed to load activity report');
    } finally {
      setLoading(false);
    }
  }, [days, selectedUser, includeDaily]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Get unique users for filter dropdown
  const users = report?.by_user || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-virgo-gold-400">Activity Tracking</h2>
          <p className="text-virgo-teal-300 text-sm mt-1">
            Monitor team activity and productivity
          </p>
        </div>
        <button
          onClick={loadReport}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-virgo-navy-700 rounded-lg border border-virgo-navy-600">
        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Date Range</label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">User Filter</label>
          <select
            value={selectedUser || ''}
            onChange={(e) => setSelectedUser(e.target.value || null)}
            className="bg-virgo-navy-800 border border-virgo-navy-500 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
          >
            <option value="">All Users</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.display_name || user.username}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-2 text-virgo-teal-300 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDaily}
              onChange={(e) => setIncludeDaily(e.target.checked)}
              className="w-4 h-4 rounded border-virgo-navy-500 bg-virgo-navy-800 text-virgo-teal-500 focus:ring-virgo-teal-500"
            />
            Include daily breakdown
          </label>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-virgo-teal-400">Loading activity data...</div>
        </div>
      )}

      {/* Summary Cards */}
      {!loading && report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Active Time */}
            <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Activity className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="text-sm text-virgo-teal-400">Total Active Time</div>
                  <div className="text-2xl font-bold text-white">
                    {report.total_active_hours.toFixed(1)}h
                  </div>
                </div>
              </div>
              <div className="text-xs text-virgo-teal-500">
                Heartbeat received within 5 minutes
              </div>
            </div>

            {/* Idle Time */}
            <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <div className="text-sm text-virgo-teal-400">Total Idle Time</div>
                  <div className="text-2xl font-bold text-white">
                    {report.total_idle_hours.toFixed(1)}h
                  </div>
                </div>
              </div>
              <div className="text-xs text-virgo-teal-500">
                30+ minutes without activity
              </div>
            </div>

            {/* Offline Time */}
            <div className="bg-virgo-navy-700 rounded-lg p-6 border border-virgo-navy-600">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <div className="text-sm text-virgo-teal-400">Total Offline Time</div>
                  <div className="text-2xl font-bold text-white">
                    {report.total_offline_hours.toFixed(1)}h
                  </div>
                </div>
              </div>
              <div className="text-xs text-virgo-teal-500">
                1+ hour idle or logged out
              </div>
            </div>
          </div>

          {/* User Activity Table */}
          <div className="bg-virgo-navy-700 rounded-lg border border-virgo-navy-600 overflow-hidden">
            <div className="px-6 py-4 border-b border-virgo-navy-600">
              <h3 className="text-lg font-semibold text-virgo-teal-400 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Activity by User
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-virgo-navy-600">
                <thead className="bg-virgo-navy-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Active
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                        Idle
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Offline
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Activity Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-virgo-navy-600">
                  {report.by_user.map((user) => {
                    const totalSeconds = user.active_seconds + user.idle_seconds + user.offline_seconds;
                    const activityRate = totalSeconds > 0
                      ? ((user.active_seconds / totalSeconds) * 100).toFixed(1)
                      : 0;

                    return (
                      <tr key={user.user_id} className="hover:bg-virgo-navy-600/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            {user.display_name || user.username}
                          </div>
                          <div className="text-xs text-virgo-teal-500">
                            @{user.username}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-green-400 font-medium">
                            {formatDuration(user.active_seconds)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-yellow-400 font-medium">
                            {formatDuration(user.idle_seconds)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-red-400 font-medium">
                            {formatDuration(user.offline_seconds)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-virgo-navy-800 rounded-full overflow-hidden max-w-[100px]">
                              <div
                                className="h-full bg-green-500 transition-all"
                                style={{ width: `${activityRate}%` }}
                              />
                            </div>
                            <span className="text-sm text-virgo-teal-300 min-w-[45px]">
                              {activityRate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {report.by_user.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-virgo-teal-500">
                        No activity data available for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily Breakdown */}
          {includeDaily && report.daily_breakdown && report.daily_breakdown.length > 0 && (
            <div className="bg-virgo-navy-700 rounded-lg border border-virgo-navy-600 overflow-hidden">
              <div className="px-6 py-4 border-b border-virgo-navy-600">
                <h3 className="text-lg font-semibold text-virgo-teal-400 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Daily Breakdown
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-virgo-navy-600">
                  <thead className="bg-virgo-navy-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                        Active
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                        Idle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                        Offline
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-virgo-navy-600">
                    {report.daily_breakdown.map((day) => (
                      <tr key={day.date} className="hover:bg-virgo-navy-600/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {new Date(day.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-green-400">
                          {formatDuration(day.active_seconds)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-yellow-400">
                          {formatDuration(day.idle_seconds)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-red-400">
                          {formatDuration(day.offline_seconds)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info box */}
      <div className="p-4 bg-virgo-navy-800 rounded border border-virgo-teal-500/30">
        <p className="text-virgo-navy-100 text-sm">
          <strong className="text-virgo-teal-400">How activity is tracked:</strong>
        </p>
        <ul className="mt-2 space-y-1 text-virgo-navy-200 text-sm">
          <li><span className="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span><strong>Active:</strong> Heartbeat received within the last 5 minutes</li>
          <li><span className="w-2 h-2 bg-yellow-500 rounded-full inline-block mr-2"></span><strong>Idle:</strong> 30+ minutes since last activity while still online</li>
          <li><span className="w-2 h-2 bg-red-500 rounded-full inline-block mr-2"></span><strong>Offline:</strong> 1+ hour without activity or explicitly logged out</li>
        </ul>
      </div>
    </div>
  );
};

export default ActivityTrackingPanel;
